import {
  Alert,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertIcon,
  Button,
  ButtonGroup,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Text,
  Tooltip,
  useDisclosure,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRef } from "react";
import { FormProvider, useForm } from "react-hook-form";

import { Restricted } from "@/app/components/Restricted";
import { CopyInput } from "@/common/components/CopyInput";
import FormInput from "@/common/components/Form/FormInput";
import FormReset from "@/common/components/Form/FormReset";
import { FormRootError } from "@/common/components/Form/FormRootError";
import FormSubmit from "@/common/components/Form/FormSubmit";
import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import { commonQueryKeys } from "@/common/data/commonQueryKeys";
import useApiForm from "@/common/hooks/useApiForm";
import { useErrorToast } from "@/common/hooks/useErrorToast";
import api from "@/common/utils/api";
import {
  AppDTO,
  PatchAppArgsSchema,
  PatchAppArgsType,
} from "@reflag/shared/appAPI";
import { z } from "@reflag/shared/utils/zod";

export default function GeneralApp() {
  const { app, org } = useEnvironment();

  const toast = useToast();
  const queryClient = useQueryClient();

  const { form, handleSubmit } = useApiForm(
    (data: PatchAppArgsType) =>
      api
        .patch<"/apps/:appId">(`/apps/${app.id}`, data)
        .then((res) => res.data),
    PatchAppArgsSchema,
    {
      onSuccess: (data) => {
        toast({
          title: "Settings saved",
          status: "success",
        });
        queryClient.invalidateQueries({
          queryKey: commonQueryKeys.bootstrap(org.id),
        });
        form.reset({ name: data.name });
      },
    },
    {
      defaultValues: {
        name: app.name,
      },
    },
  );

  const confirmDisclosure = useDisclosure();
  const canDeleteApp = org.apps.length > 1;

  return (
    <Flex direction="column" gap={4} maxW="mediumForm">
      <Restricted alertStyle="callout" role="admin">
        <Flex direction="column" gap={4}>
          <form onSubmit={handleSubmit}>
            <FormProvider {...form}>
              <VStack align="flex-start" minW="compactForm" spacing={4}>
                <FormInput
                  autoComplete="off"
                  label="App name"
                  name="name"
                  w="full"
                />

                <FormRootError />

                <ButtonGroup>
                  <FormSubmit showPostSubmit />
                  <FormReset />
                </ButtonGroup>
              </VStack>
            </FormProvider>
          </form>

          <Divider />

          <FormControl w="auto">
            <FormLabel>App ID</FormLabel>
            <CopyInput buttonLabel="Copy app ID" value={app.id} />
          </FormControl>

          <Divider />

          <VStack align="flex-start" spacing={4}>
            <Heading as="h2" size="sm">
              Danger zone
            </Heading>
            <Tooltip
              isDisabled={canDeleteApp}
              label="The last app in the organization cannot be deleted"
            >
              <Button
                colorScheme="red"
                isDisabled={confirmDisclosure.isOpen || !canDeleteApp}
                loadingText="Deleting..."
                type="button"
                variant="solid"
                onClick={confirmDisclosure.onOpen}
              >
                Delete App
              </Button>
            </Tooltip>
            <ConfirmAppDeleteDialog app={app} disclosure={confirmDisclosure} />
          </VStack>
        </Flex>
      </Restricted>
    </Flex>
  );
}

function ConfirmAppDeleteDialog({
  app,
  disclosure,
}: {
  app: Pick<AppDTO, "id" | "name">;
  disclosure: ReturnType<typeof useDisclosure>;
}) {
  const { clearStoredAppEnv } = useEnvironment();
  const errorToast = useErrorToast();

  const deleteMutation = useMutation({
    mutationKey: ["deleteApp", app.id],
    mutationFn: () => api.delete<"/apps/:appId">(`/apps/${app.id}`),
    onSuccess: () => {
      clearStoredAppEnv();
      window.location.href = "/";
    },
    onError: (error: any) => {
      errorToast({
        title: `Failed to delete '${app.name}' app`,
        description: error.response?.data.error.message,
      });

      form.reset();
    },
  });

  const form = useForm<{ name: string }>({
    reValidateMode: "onSubmit",
    mode: "onSubmit",
    resolver: zodResolver(
      z.object({
        name: z.literal(app.name, { message: "Please confirm the app name" }),
      }),
    ),
  });

  function handleClose() {
    disclosure.onClose();
    form.reset();
  }

  const cancelRef = useRef(null);

  return (
    <AlertDialog
      isOpen={disclosure.isOpen}
      leastDestructiveRef={cancelRef}
      onClose={handleClose}
    >
      <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogHeader>Delete App</AlertDialogHeader>
          <AlertDialogBody align="flex-start" as={VStack} spacing={4}>
            <Text as="p">
              Are you sure you want to delete <strong>{app.name}</strong>?
            </Text>

            <Alert alignItems="flex-start" status="warning">
              <AlertIcon />

              <VStack alignItems="flex-start" spacing={4}>
                <Text as="p">
                  All tracked events, users and companies on the apps tracking
                  key will be deleted.
                </Text>

                <Text as="p">You can&apos;t undo this action afterwards.</Text>
              </VStack>
            </Alert>

            <Text as="p">
              Please type <strong>{app.name}</strong> to confirm deletion.
            </Text>

            <form
              id="app-delete-modal"
              style={{
                width: "100%",
              }}
              onSubmit={form.handleSubmit(() => deleteMutation.mutateAsync())}
            >
              <FormProvider {...form}>
                <FormInput
                  autoComplete="off"
                  name="name"
                  width="100%"
                  data-1p-ignore
                ></FormInput>
              </FormProvider>
            </form>
          </AlertDialogBody>
          <AlertDialogFooter>
            <ButtonGroup>
              <Button
                ref={cancelRef}
                isDisabled={form.formState.isSubmitting}
                variant="ghostDimmed"
                onClick={handleClose}
              >
                Cancel
              </Button>
              <Button
                colorScheme="red"
                form="app-delete-modal"
                isLoading={form.formState.isSubmitting}
                type="submit"
              >
                Delete
              </Button>
            </ButtonGroup>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
}
